import { SessionSnapshot } from "../types";

export default class Session {
  private _id: string;
  private _userId: string | undefined;
  private _initialTimeStamp: number;
  private _currentInitialTimeStamp: number;
  private _lastUpdatedTimeStamp: number;
  private _lastUpdatedById: string;
  private _timeSpent: number;
  private _totalTimeSpent: number;

  private _snapshots: SessionSnapshot[] = [];

  constructor(id: string, userId?: string) {
    this._id = id;
    this._userId = userId;
    this._initialTimeStamp = Date.now();
    this._currentInitialTimeStamp = this._initialTimeStamp;
    this._lastUpdatedTimeStamp = this._initialTimeStamp;
    this._lastUpdatedById = id;
    this._timeSpent = 0;
    this._totalTimeSpent = 0;
  }

  get id() {
    return this._id;
  }

  get userId() {
    return this._userId;
  }

  get initialTimeStamp() {
    return this._initialTimeStamp;
  }

  get lastUpdatedTimeStamp() {
    return this._lastUpdatedTimeStamp;
  }

  get lastUpdatedById() {
    return this._lastUpdatedById;
  }

  get timeSpent(): number {
    return this._timeSpent;
  }

  get totalTimeSpent(): number {
    return this._totalTimeSpent;
  }

  get snapshot(): SessionSnapshot {
    return {
      id: this._id,
      userId: this._userId,
      initialTimeStamp: this._currentInitialTimeStamp,
      currentInitialTimeStamp: this._currentInitialTimeStamp,
      lastUpdatedTimeStamp: this._lastUpdatedTimeStamp,
      lastUpdatedById: this._lastUpdatedById,
      timeSpent: this._timeSpent,
      totalTimeSpent: this._totalTimeSpent,
    };
  }

  reconnect(newTimestamp: number, updatedBy: string) {
    this._snapshots.push(this.snapshot);
    this._currentInitialTimeStamp = newTimestamp;
    this._lastUpdatedTimeStamp = this._currentInitialTimeStamp;
    this._lastUpdatedById = updatedBy;
    this._timeSpent = 0;
  }

  update(newTimestamp: number, updatedBy: string) {
    this._totalTimeSpent +=  newTimestamp - this._lastUpdatedTimeStamp;
    this._lastUpdatedTimeStamp = newTimestamp;
    this._lastUpdatedById = updatedBy;
    this._timeSpent = this._lastUpdatedTimeStamp - this._currentInitialTimeStamp;
  }

  private fromSnapshot(snapshot: SessionSnapshot): Session {
    this._initialTimeStamp = snapshot.initialTimeStamp;
    this._currentInitialTimeStamp = snapshot.currentInitialTimeStamp;
    this._lastUpdatedTimeStamp = snapshot.lastUpdatedTimeStamp;
    this._lastUpdatedById = snapshot.lastUpdatedById;
    this._timeSpent = snapshot.timeSpent;
    this._totalTimeSpent = snapshot.totalTimeSpent;
    this._snapshots = snapshot.snapshots || [];

    return this;
  }

  static fromJson(jsonString: string): Session {
    const snapshot = JSON.parse(jsonString) as SessionSnapshot;
    return new Session(snapshot.id, snapshot.userId).fromSnapshot(snapshot);
  }

  toJson(): string {
    return JSON.stringify({
      id: this._id,
      userId: this._userId,
      initialTimeStamp: this._initialTimeStamp,
      currentInitialTimeStamp: this._currentInitialTimeStamp,
      lastUpdatedTimeStamp: this._lastUpdatedTimeStamp,
      lastUpdatedById: this._lastUpdatedById,
      timeSpent: this._timeSpent,
      totalTimeSpent: this._totalTimeSpent,
      snapshots: this._snapshots,
    } as SessionSnapshot);
  }

  print() {
    console.table({
      'ID': this._id,
      'User ID': this._userId,
      'Initial timestamp': this._initialTimeStamp,
      'Current initial timestamp': this._currentInitialTimeStamp,
      'Last updated timestamp': this._lastUpdatedTimeStamp,
      'Last updated by': this._lastUpdatedById,
      'Time spent': this.timeSpent,
      'Total time spent': this._totalTimeSpent,
    });
  }
}